import { OptionalSpeechPreferences } from "./OptionalSpeechPreferences";
import { AudioLines, Mic, MicOff, SendHorizontal } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/components/ui/utils";
import { VoiceButton } from "@/components/ui/voice-button";
import { VoicePicker } from "@/components/ui/voice-picker";
import { useVoiceAssistant } from "@/features/voice-agent/VoiceAssistantContext";
import type { VoiceConversationState } from "@/lib/voice/conversation";
import { VoiceConversationBar } from "./VoiceConversationBar";

const VOICE_ID_STORAGE_KEY = "xan.voice.voice_id";

function isVoiceActive(status: VoiceConversationState["status"]): boolean {
  return status === "connecting" || status === "listening" || status === "speaking";
}

function StartVoiceButton({ toggle }: { toggle: () => void }) {
  return (
    <VoiceButton
      aria-label="Start voice conversation"
      className="rounded-full bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:text-primary-foreground"
      icon={<AudioLines aria-hidden="true" className="size-4" />}
      onPress={toggle}
      size="mobileTouchIcon"
      state="idle"
      waveformClassName="bg-transparent"
      variant="ghost"
    />
  );
}

function DictationButton({
  disabled,
  dictating,
  onCancel,
  start,
  title,
}: {
  disabled: boolean;
  dictating: boolean;
  onCancel: () => void;
  start: () => Promise<unknown>;
  title: string;
}) {
  if (dictating) {
    return (
      <Button
        aria-label="Cancel active dictation"
        onClick={onCancel}
        size="mobileTouchIcon"
        type="button"
        variant="ghost"
      >
        <MicOff aria-hidden="true" className="size-4" />
      </Button>
    );
  }

  return (
    <Button
      aria-label="Start dictation"
      disabled={disabled}
      onClick={() => {
        void start();
      }}
      size="mobileTouchIcon"
      title={title}
      type="button"
      variant="ghost"
    >
      <Mic aria-hidden="true" className="size-4" />
    </Button>
  );
}

function SendButton({ disabled, submit }: { disabled: boolean; submit: () => void }) {
  return (
    <Button
      aria-label="Send message"
      disabled={disabled}
      onClick={submit}
      size="mobileTouchIcon"
      type="button"
    >
      <SendHorizontal aria-hidden="true" className="size-4" />
    </Button>
  );
}

function submitOnEnter(event: KeyboardEvent<HTMLTextAreaElement>, submit: () => Promise<void>) {
  if (event.key !== "Enter") {
    return;
  }
  if (event.shiftKey) {
    return;
  }
  if (event.nativeEvent.isComposing) {
    return;
  }
  event.preventDefault();
  submit().catch(() => undefined);
}

type VoiceAssistantSession = ReturnType<typeof useVoiceAssistant>;

function useMessageSubmission({
  sendText,
  setValue,
  value,
}: {
  sendText: VoiceAssistantSession["sendText"];
  setValue: VoiceAssistantSession["setDraft"];
  value: string;
}) {
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const submit = async () => {
    const submittedDraft = value;
    const text = value.trim();
    if (!text) {
      return;
    }
    if (sendingRef.current) {
      return;
    }
    sendingRef.current = true;
    setSending(true);
    try {
      if (!(await sendText(text))) {
        return;
      }
      if (!mountedRef.current) {
        return;
      }
      setValue((current) => (current === submittedDraft ? "" : current));
    } finally {
      sendingRef.current = false;
      if (mountedRef.current) {
        setSending(false);
      }
    }
  };

  return { sending, submit };
}

export function VoiceComposer({ className }: { className?: string }) {
  const {
    speech,
    draft: value,
    mode,
    newThread,
    textPending,
    threadId,
    turns,
    levels,
    muted,
    outputMuted,
    sendText,
    setDraft: setValue,
    status,
    stop,
    toggle,
    toggleMute,
    toggleOutputMute,
  } = useVoiceAssistant();

  const [selectedVoiceId, setSelectedVoiceId] = useState<string>(() => {
    try {
      return localStorage.getItem(VOICE_ID_STORAGE_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const pickerDisabled = mode === "live" || isVoiceActive(status);

  const handleVoiceChange = (voiceId: string) => {
    if (pickerDisabled) {
      return;
    }
    setSelectedVoiceId(voiceId);
    try {
      if (voiceId) {
        localStorage.setItem(VOICE_ID_STORAGE_KEY, voiceId);
      } else {
        localStorage.removeItem(VOICE_ID_STORAGE_KEY);
      }
    } catch {
      // ignore storage error
    }
  };

  const cancelDictation = speech?.cancelDictation;
  const stopReadAloud = speech?.stopReadAloud;
  useEffect(
    () => () => {
      cancelDictation?.();
      stopReadAloud?.(true);
    },
    [cancelDictation, stopReadAloud],
  );
  const { sending, submit } = useMessageSubmission({
    sendText,
    setValue,
    value,
  });
  const dictating = speech?.isDictating() ?? false;
  const active = mode !== "text" && isVoiceActive(status);
  const canStartDictation = Boolean(
    speech &&
    speech.preferences.dictation &&
    speech.capabilities.dictation.available &&
    mode === "text" &&
    !textPending &&
    !dictating &&
    !["loading", "playing"].includes(speech.state.readAloud),
  );
  const dictationTitle = !speech?.preferences.dictation
    ? "Enable dictation in speech settings first."
    : !speech.capabilities.dictation.available
      ? (speech.capabilities.dictation.reason ?? "Dictation is unavailable.")
      : mode !== "text"
        ? "Return to text chat before dictating."
        : textPending
          ? "Wait for the text response to finish before dictating."
          : ["loading", "playing"].includes(speech.state.readAloud)
            ? "Stop read aloud before dictating."
            : "Start dictation";

  return (
    <div
      className={cn(
        "relative rounded-2xl border bg-background/95 pb-[env(safe-area-inset-bottom,0px)] shadow-xl backdrop-blur",
        className,
      )}
    >
      {(mode === "live" || textPending) && (
        <p aria-live="polite" className="px-4 pt-2 text-xs text-muted-foreground">
          {mode === "live" ? "Live voice · mute does not pause billing" : "Text chat · thinking…"}
        </p>
      )}
      {mode === "text" && textPending && (
        <Button
          onClick={() => {
            void stop();
          }}
          type="button"
          variant="ghost"
        >
          Cancel text response
        </Button>
      )}
      {mode === "text" && !textPending && (turns.length > 0 || threadId) && newThread && (
        <Button
          onClick={() => {
            void newThread();
          }}
          type="button"
          variant="ghost"
        >
          New thread
        </Button>
      )}
      {mode === "live" && !active && (
        <Button
          onClick={() => {
            void stop();
          }}
          type="button"
          variant="ghost"
        >
          Return to text chat
        </Button>
      )}
      {active && (
        <VoiceConversationBar
          levels={levels}
          muted={muted}
          outputMuted={outputMuted}
          onEnd={stop}
          onToggleMute={toggleMute}
          onToggleOutputMute={toggleOutputMute}
          status={status}
        />
      )}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-1.5 pr-2.5">
        <Textarea
          aria-label="Message voice assistant"
          className="min-h-14 min-w-0 border-0 bg-transparent py-4 pr-2 pl-4 shadow-none"
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => submitOnEnter(event, submit)}
          placeholder="Ask anything…"
          rows={1}
          value={value}
        />
        <div className="mb-2.5 flex shrink-0 gap-1.5">
          {speech && <OptionalSpeechPreferences speech={speech} />}
          {speech && (
            <DictationButton
              dictating={dictating}
              disabled={!canStartDictation}
              onCancel={speech.cancelDictation}
              start={speech.startDictation}
              title={dictationTitle}
            />
          )}
          {!active && !dictating && <StartVoiceButton toggle={toggle} />}
          <SendButton
            disabled={!value.trim() || sending || Boolean(textPending) || dictating}
            submit={() => submit().catch(() => undefined)}
          />
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-border/20 px-3.5 py-1.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium text-foreground/70">Voice</span>
          <VoicePicker
            className="h-7 border-border/40 bg-muted/20 px-2.5 py-0.5 text-xs hover:bg-muted/50 rounded-full shadow-none font-normal"
            disabled={pickerDisabled}
            onValueChange={handleVoiceChange}
            value={selectedVoiceId}
          />
        </div>
        <span className="text-[11px] opacity-70">ElevenLabs Conversational AI</span>
      </div>
    </div>
  );
}
